import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  ExamTypeEnum,
  RisrAssessActionDisplayNames,
  RisrAssessExam,
  RisrAssessExamStates,
  RisrAssessExamStatesDisplayNames
} from '@fry/connections/lib/connection';
import { BookingsStore } from "@fry/booking/lib/bookings";
import { NotifyService } from "@fry/lib/utils";
import { filter, interval, Subject, takeUntil } from 'rxjs';


@Component({
  selector: 'eas-risr-assess-exam',
  templateUrl: './risr-assess-exam.component.html',
  standalone: false,
})
export class RisrAssessExamComponent implements OnInit, OnDestroy {
  @Input() bookingId: string;
  @Input() bookingData: any;
  @Input() risrAssessExam: RisrAssessExam;
  @Input() risrAssessExamType: ExamTypeEnum;

  isOwner: boolean = false;

  protected readonly RisrAssessExamStates = RisrAssessExamStates;
  protected readonly RisrAssessExamStatesDisplayNames = RisrAssessExamStatesDisplayNames;
  private destroy$ = new Subject<void>(); // Observable to handle cleanup for the interval


  constructor(private bookings: BookingsStore, private notify: NotifyService) {
    this.bookings = bookings;
  }

  ngOnInit(): void {
    this.isOwner = !!this.risrAssessExam.username; // we assume that if it's been removed from the backend therefore is not the owner.

    this.setState();
    this.handleAutoRefresh();
  }

  ngOnDestroy(): void {
    this.destroy$.next(); // Emit to terminate the subscription
    this.destroy$.complete(); // Complete the Subject
  }

  private setState(): void {
    if (this.risrAssessExam?.error) {
      this.risrAssessExam = {
        ...this.risrAssessExam,
        state: RisrAssessExamStates.ERROR,
        errorMessage: `An error occurred while ${RisrAssessActionDisplayNames[this.risrAssessExam.error.action]}`
      };
    } else if (
      [RisrAssessExamStates.CREATED, RisrAssessExamStates.PUSHED].includes(this.risrAssessExam?.state)
      && !this.isStartDateTimePassed()
    ) {
      this.risrAssessExam.state = RisrAssessExamStates.WAITING_FOR_CREATION;
    }
  }

  private isStartDateTimePassed(): boolean {
    if (!this.risrAssessExam?.startDateTimeFieldId || !this.bookingData) {
      return true; // Default to "start time passed" if needed fields are missing
    }

    const startDateTimeUTC = this.bookingData[this.risrAssessExam.startDateTimeFieldId];
    const startDateTime = new Date(startDateTimeUTC); // Convert string to a Date object
    const nowUTC = new Date(); // Current date in the browser's timezone

    if (isNaN(startDateTime.getTime())) {
      console.error('Invalid startDateTime field:', startDateTimeUTC);
      return true; // Consider "start time passed" if date parsing fails
    }

    return startDateTime <= nowUTC; // Compare dates
  }


  private handleAutoRefresh(): void {
    // if (!this.shouldRefresh()) {
    //   return;
    // }

    let REFRESH_INTERVAL_MS = 10000;
    interval(REFRESH_INTERVAL_MS)
      .pipe(
        takeUntil(this.destroy$), // Automatically stop when component is destroyed
        filter(() => this.shouldRefresh()), // Refresh only for specified states
      )
      .subscribe(() => this.refreshRisrAssessExam()); // Trigger refresh
  }

  private shouldRefresh(): boolean {
    const isWaitingForCreation = this.risrAssessExam.state === RisrAssessExamStates.WAITING_FOR_CREATION;
    const isStartDateTimePassed = this.isStartDateTimePassed();
    new Date()
    return isWaitingForCreation ? isStartDateTimePassed : true;
  }


  refreshRisrAssessExam(): void {
    this.bookings.getRisrAssessExam(this.bookingId, this.risrAssessExam.id).subscribe({
      next: (updatedExam) => {
        console.log("Updated exam:")
        this.risrAssessExam = {
          ...this.risrAssessExam, // Retain existing data
          ...updatedExam,        // Apply updated values
        };
        this.setState();
      },
      error: (error) => {
        this.notify.error('Failed to refresh the exam: ' + error);
      },
    });
  }

  startExam() {
    if (this.risrAssessExam.state === RisrAssessExamStates.PUSHED) {
      this.bookings.startRisrAssessExam(this.bookingId, this.risrAssessExam.id).subscribe({
        next: () => {
          if (this.risrAssessExam.url) {
            this.openUrl(this.risrAssessExam.url)
          }
        },
        error: (error) => {
          this.notify.error('Failed to start the exam: ' + error);
          throw error;
        }
      });
    } else if (this.risrAssessExam.state === RisrAssessExamStates.STARTED) {
      this.openUrl(this.risrAssessExam.url)
    }
  }

  openResultEventUrl(url: string) {
    this.openUrl(url)
  }

  openUrl(url: string | undefined) {
    if (!url) {
      return;
    }
    window.open(url, '_blank');
  }

  protected readonly ExamTypeEnum = ExamTypeEnum;
}
